import type {
  ListingPublishReadinessInput,
  ListingPublishReadinessItem,
  ListingReadinessTab,
  ListingReadinessTier,
} from "./listing-readiness";

function text(value: string | null | undefined): boolean {
  return Boolean(value?.trim());
}

function positive(value: bigint | number | string | null | undefined): boolean {
  if (value == null) return false;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return false;
    const amount = Number(trimmed);
    return Number.isFinite(amount) && amount > 0;
  }
  if (typeof value === "bigint") return value > BigInt(0);
  return Number.isFinite(value) && value > 0;
}

function propertyReady(input: ListingPublishReadinessInput): boolean {
  if (input.propertyKind === "NONE" || input.propertyKind === "OWNED") return true;
  if (input.propertyKind !== "RENTED") return false;
  const leaseEnd =
    input.leaseExpiration instanceof Date
      ? input.leaseExpiration.toISOString()
      : input.leaseExpiration;
  return positive(input.rentAmount) && text(leaseEnd);
}

function financingReady(input: ListingPublishReadinessInput): boolean {
  if (!input.financingAvailable) return false;
  return Boolean(
    input.sbaPrequalified ||
      input.buyerCanAssumeLoan ||
      positive(input.minimumDownPayment),
  );
}

function row(
  id: string,
  label: string,
  todo: string,
  tier: ListingReadinessTier,
  done: boolean,
  tab: ListingReadinessTab,
  anchor: string,
): ListingPublishReadinessItem {
  return { id, label, todo, tier, done, tab, anchor };
}

export function publishReadinessItems(
  input: ListingPublishReadinessInput,
): ListingPublishReadinessItem[] {
  const established =
    input.establishedAt instanceof Date
      ? input.establishedAt.toISOString()
      : input.establishedAt;

  return [
    row("businessType", "Business type", "Choose the business type", "required", input.businessTypeId != null, "details", "listing-type"),
    row("saleType", "Sale structure", "Choose the sale structure", "required", text(input.saleType), "details", "listing-sale"),
    row("location", "Location", "Add the city and state", "required", text(input.city) && text(input.state), "details", "listing-location"),
    row("establishedYear", "Year established", "Add the year established", "required", text(established), "details", "listing-established"),
    row("pitch", "Pitch", "Write a short pitch", "required", text(input.pitch), "details", "listing-pitch"),
    row("description", "Description", "Write the description", "required", text(input.description), "details", "listing-description"),
    row("askingPrice", "Asking price", "Add the asking price", "required", positive(input.askingPrice), "financials", "listing-asking-price"),
    row(
      "financials",
      "Financials",
      "Add revenue, EBITDA, seller's discretionary earnings, or profit",
      "required",
      positive(input.revenue) || positive(input.ebitda) || positive(input.sde) || positive(input.profit),
      "financials",
      "listing-revenue",
    ),
    row("photos", "Photos", "Upload at least one photo", "required", input.photoCount >= 1, "photos", "listing-photos"),
    row("photoSet", "Photo set", "Add at least 5 photos", "great", input.photoCount >= 5, "photos", "listing-photos"),
    row("property", "Property", "Say whether the location is owned, leased, or neither", "great", propertyReady(input), "financials", "listing-property"),
    row("employees", "Employees", "Add the employee count", "great", positive(input.employeeCount), "details", "listing-employees"),
    row("ownerHours", "Owner hours", "Add the owner's weekly hours", "great", positive(input.ownerWeeklyHours), "details", "listing-hours"),
    row("photoGallery", "Photo gallery", "Add at least 10 photos", "excellent", input.photoCount >= 10, "photos", "listing-photos"),
    row("financing", "Financing", "Add seller financing details", "excellent", financingReady(input), "financials", "listing-financing"),
    row("training", "Training", "Describe the training you will provide", "excellent", Boolean(input.trainingAvailable && text(input.trainingDuration)), "financials", "listing-training"),
    row("website", "Website", "Add the business website", "excellent", text(input.websiteUrl), "details", "listing-website"),
  ];
}
